import { clearAudiobook, selectAudiobook } from "@app/store/audiobook";
import { fancyTimeFormat } from "@app/utils/util";
import { Images } from "@assets/images";
import clsx from "clsx";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

function Audiobook() {
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isMute, setIsMute] = useState(false);
  const [volumn, setVolumn] = useState(100);
  const [oldVolumn, setOldVolumn] = useState(100);
  const [currentLinkAudio, setCurrentLinkAudio] = useState<string>();
  const [duration, setDuration] = useState<number>();
  const [currentTime, setCurrentTime] = useState<number>();
  const [percentTime, setPercentTime] = useState(0);
  const [isPause, setIsPause] = useState(false);

  const audiobookState = useSelector(selectAudiobook);

  const dispatch = useDispatch();

  useEffect(() => {
    if (audiobookState.audiobookData.linkAudio) {
      setCurrentLinkAudio(audiobookState.audiobookData.linkAudio);
      setIsPause(false);

      if (audioRef.current) {
        audioRef.current.load();
        audioRef.current.play();
      }
    }
  }, [audiobookState.audiobookData]);

  const handlePanelCloseClick = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    setCurrentLinkAudio(undefined);
    dispatch(clearAudiobook());
  };

  const handleVolumnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (Number(event.target.value) === 0) {
      setIsMute(true);
    } else {
      setIsMute(false);
    }

    setVolumn(Number(event.target.value));
    setOldVolumn(Number(event.target.value));

    if (audioRef.current) {
      audioRef.current.volume = Number(event.target.value) / 100;
    }
  };

  const handleVolumnClick = () => {
    if (!isMute) {
      setVolumn(0);

      if (audioRef.current) {
        audioRef.current.volume = 0;
      }
    } else {
      setVolumn(oldVolumn);

      if (audioRef.current) {
        audioRef.current.volume = oldVolumn / 100;
      }
    }

    setIsMute(!isMute);
  };

  const handleDurationChange = (e: ChangeEvent<HTMLAudioElement>) => {
    if (!isNaN(e.target.duration)) {
      setDuration(e.target.duration);
    }
  };

  const handleTimeUpdate = (e: ChangeEvent<HTMLAudioElement>) => {
    if (!isNaN(e.target.currentTime)) {
      setCurrentTime(e.target.currentTime);
    }

    if (!isNaN(e.target.currentTime) && duration) {
      const percentTime = (e.target.currentTime / duration) * 100;

      setPercentTime(percentTime);
    }

    if (e.target.ended) {
      setIsPause(true);
    }
  };

  const handlePercentTimeChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (duration) {
      setPercentTime(Number(e.target.value));

      if (audioRef.current) {
        audioRef.current.currentTime =
          (Number(e.target.value) / 100) * duration;
      }
    }
  };

  const handlePauseClick = () => {
    setIsPause(!isPause);

    if (audioRef.current) {
      if (!isPause) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleBackwardClick = () => {
    if (typeof currentTime === "number" && duration) {
      const nextTime = currentTime - 10 <= 0 ? 0 : currentTime - 10;

      const percentTime = (nextTime / duration) * 100;

      setPercentTime(percentTime);

      if (audioRef.current) {
        audioRef.current.currentTime = nextTime;
      }
    }
  };

  const handleForwardClick = () => {
    if (typeof currentTime === "number" && duration) {
      const nextTime =
        currentTime + 10 >= duration ? duration : currentTime + 10;

      const percentTime = (nextTime / duration) * 100;

      setPercentTime(percentTime);

      if (audioRef.current) {
        audioRef.current.currentTime = nextTime;
      }
    }
  };

  return (
    <div
      className={clsx(
        "fixed bottom-0 left-0 w-full h-[108px] select-none",
        "bg-[rgba(23,26,32,0.90)] backdrop-blur-[18px]",
        "lg:px-[100px] md:px-10 py-4 text-white z-10 hidden",
        {
          "!block": !!audiobookState.isPanelShow,
        }
      )}
    >
      <div className="h-full flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 cursor-pointer"></div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-4 justify-center items-center">
            <img
              className="size-11 p-[10px] hover:bg-gray-500 hover:rounded-full cursor-pointer"
              src={Images.BackwardIcon.default}
              alt=""
              onClick={handleBackwardClick}
            />
            <img
              className="cursor-pointer"
              src={
                !isPause ? Images.PauseIcon.default : Images.PlayIcon.default
              }
              alt=""
              onClick={handlePauseClick}
            />
            <img
              className="size-11 p-[10px] hover:bg-gray-500 hover:rounded-full cursor-pointer"
              src={Images.ForwardIcon.default}
              alt=""
              onClick={handleForwardClick}
            />
          </div>
          <div className="flex gap-2 items-center justify-center">
            <div className="font-[SVN-Gilroy] text-[#DFE2E7] text-xs font-medium tracking-[0.024px]">
              {typeof currentTime === "number"
                ? fancyTimeFormat(currentTime)
                : "00:00"}
            </div>
            <div className="xl:w-[556px] lg:w-[350px] md:w-[180px] flex">
              <input
                type="range"
                min={0}
                max={100}
                value={percentTime}
                onChange={handlePercentTimeChange}
                className={clsx(
                  "w-full appearance-none bg-transparent [&::-webkit-slider-runnable-track]:h-[6px]",
                  "[&::-webkit-slider-thumb]:translate-y-[-31%] [&::-webkit-slider-runnable-track]:rounded-full",
                  "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#FFFFFF]",
                  "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-[16px]",
                  "[&::-webkit-slider-runnable-track]:bg-[#5570f1]"
                )}
              />
            </div>
            <div className="font-[SVN-Gilroy] text-[#DFE2E7] text-xs font-medium tracking-[0.024px]">
              {duration ? fancyTimeFormat(duration) : "00:00"}
            </div>
          </div>
        </div>
        <div className="flex gap-5">
          <div className="flex gap-3">
            <img
              className="cursor-pointer hover:rounded-full hover:bg-gray-500"
              src={
                !isMute
                  ? Images.VolumnIcon.default
                  : Images.VolumnCrossIcon.default
              }
              alt=""
              onClick={handleVolumnClick}
            />
            <input
              type="range"
              min={0}
              max={100}
              value={volumn}
              onChange={handleVolumnChange}
              className={clsx(
                "w-[84px] appearance-none bg-transparent [&::-webkit-slider-runnable-track]:h-[6px]",
                "[&::-webkit-slider-thumb]:translate-y-[-31%] [&::-webkit-slider-runnable-track]:rounded-full",
                "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#FFFFFF]",
                "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-[16px]",
                "[&::-webkit-slider-runnable-track]:bg-[#5570f1]"
              )}
            />
          </div>
        </div>
      </div>
      <div
        className="absolute top-3 right-3 cursor-pointer hover:bg-gray-500 hover:rounded-full"
        onClick={handlePanelCloseClick}
      >
        <img src={Images.CloseIcon.default} alt="" />
      </div>
      <audio
        ref={audioRef}
        hidden
        onTimeUpdate={handleTimeUpdate}
        onDurationChange={handleDurationChange}
      >
        <source src={currentLinkAudio} type="" />
      </audio>
    </div>
  );
}

export default Audiobook;
